import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { format } from 'date-fns';
import type { DiveSite } from '../types/DiveSite';
import type { ScheduledDive } from '../types/ScheduledDive';
import { scheduledDivesService } from '../services/scheduledDivesService';
import { useNotification } from './NotificationContext';
import DiveSitePickerField from './DiveSitePickerField';
import { buddyService, DiveBuddy, UserPublicProfile } from '../services/buddyService';
import { authService } from '../services/authService';
import { useGroups } from '../hooks/useGroups';
import { useClubs } from '../hooks/useClubs';

// "Not open" plus the two open-to modes — mirrors PostComposer's AudienceMode approach.
const OpenTo = {
  Nobody: 'Nobody',
  Group: 'Group',
  Club: 'Club',
} as const;
type OpenToValue = typeof OpenTo[keyof typeof OpenTo];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (dive: ScheduledDive) => void;
  editing?: ScheduledDive | null;
  /** Pre-fill the date when creating (yyyy-MM-dd), e.g. from a clicked calendar day */
  initialDate?: string | null;
  /** Pre-select "open to" a specific group/club, e.g. opened from that group's/club's own page. */
  initialOpenTo?: { groupId: string } | { clubId: string } | null;
  /**
   * Attach this dive to an existing DiveTrip (opened from that trip's own page) — distinct
   * from TripDiveEntry/tripDives below, which is the older same-day multi-tank grouping.
   * When set, the open-to audience is inherited from the trip and isn't chosen here.
   */
  initialTripId?: string | null;
}

// Slice the date portion directly — avoids local-timezone shift turning midnight UTC into the previous day
const toLocalDateString = (iso: string) => iso.slice(0, 10);
const todayString = () => format(new Date(), 'yyyy-MM-dd');

interface TripDiveEntry {
  siteName: string;
  linkedSiteSlug: string | null;
}

const newSessionId = () =>
  `ses-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const ScheduleDiveDialog: React.FC<Props> = ({ open, onClose, onSaved, editing, initialDate, initialOpenTo, initialTripId }) => {
  const { showError } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(todayString());
  const [siteName, setSiteName] = useState('');
  const [linkedSiteSlug, setLinkedSiteSlug] = useState<string | null>(null);
  const [charterCompany, setCharterCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [buddyOptions, setBuddyOptions] = useState<UserPublicProfile[]>([]);
  const [invitedBuddies, setInvitedBuddies] = useState<UserPublicProfile[]>([]);
  // Additional dives in the same outing (e.g. the second tank of a two-tank trip)
  const [tripDives, setTripDives] = useState<TripDiveEntry[]>([]);
  const [openTo, setOpenTo] = useState<OpenToValue>(OpenTo.Nobody);
  const [openToGroupId, setOpenToGroupId] = useState('');
  const [openToClubId, setOpenToClubId] = useState('');

  const currentUserId = authService.getUser()?.userId ?? null;
  const { groups } = useGroups(currentUserId);
  const { clubs } = useClubs(currentUserId);

  useEffect(() => {
    if (open) {
      // Load buddy list for invite selector
      const userId = authService.getUser()?.userId;
      if (userId) {
        buddyService.getBuddies(userId).then(async (buddies: DiveBuddy[]) => {
          const profiles = await Promise.all(
            buddies.map((b: DiveBuddy) => buddyService.getUserProfile(b.buddyUserId).catch(() => null))
          );
          const loadedOptions = profiles.filter((p): p is UserPublicProfile => p !== null);
          setBuddyOptions(loadedOptions);

          // Pre-select buddies already invited to this dive, once we know their profiles
          if (editing) {
            const inviteeIds = new Set(editing.invitees.map((i) => i.userId));
            setInvitedBuddies(loadedOptions.filter((o) => inviteeIds.has(o.userId)));
          }
        }).catch(() => {});
      }

      if (editing) {
        setDate(toLocalDateString(editing.scheduledDate));
        setSiteName(editing.siteName ?? '');
        setLinkedSiteSlug(editing.extra?.linkedSiteSlug ?? null);
        setCharterCompany(editing.charterCompany ?? '');
        setNotes(editing.notes ?? '');
        setInvitedBuddies([]);
        setTripDives([]);
        // Who a dive is open to is set once at creation and isn't editable afterward —
        // changing it once there may already be invitees/joiners is out of scope for now.
        setOpenTo(editing.openToGroupId ? OpenTo.Group : editing.openToClubId ? OpenTo.Club : OpenTo.Nobody);
        setOpenToGroupId(editing.openToGroupId ?? '');
        setOpenToClubId(editing.openToClubId ?? '');
      } else {
        setDate(initialDate || todayString());
        setSiteName('');
        setLinkedSiteSlug(null);
        setCharterCompany('');
        setInvitedBuddies([]);
        setNotes('');
        setTripDives([]);
        if (initialTripId) {
          // The trip's own audience applies to every dive under it — nothing to pick here.
          setOpenTo(OpenTo.Nobody);
          setOpenToGroupId('');
          setOpenToClubId('');
        } else if (initialOpenTo && 'groupId' in initialOpenTo) {
          setOpenTo(OpenTo.Group);
          setOpenToGroupId(initialOpenTo.groupId);
          setOpenToClubId('');
        } else if (initialOpenTo && 'clubId' in initialOpenTo) {
          setOpenTo(OpenTo.Club);
          setOpenToClubId(initialOpenTo.clubId);
          setOpenToGroupId('');
        } else {
          setOpenTo(OpenTo.Nobody);
          setOpenToGroupId('');
          setOpenToClubId('');
        }
      }
    }
  }, [open, editing, initialDate, initialOpenTo, initialTripId]);

  const handleSiteLinked = (site: DiveSite | null) => {
    setLinkedSiteSlug(site?.slug ?? null);
  };

  const handleSave = async () => {
    if (!date) {
      showError('Please select a date.');
      return;
    }
    if (!editing && !initialTripId && openTo === OpenTo.Group && !openToGroupId) {
      showError('Please choose which dive group this is open to.');
      return;
    }
    if (!editing && !initialTripId && openTo === OpenTo.Club && !openToClubId) {
      showError('Please choose which club this is open to.');
      return;
    }

    setSaving(true);
    try {
      const extra: Record<string, string> = { ...(editing?.extra ?? {}) };
      if (linkedSiteSlug) {
        extra.linkedSiteSlug = linkedSiteSlug;
      } else {
        delete extra.linkedSiteSlug;
      }

      const basePayload = {
        scheduledDate: `${date}T00:00:00.000Z`,
        siteName: siteName.trim() || undefined,
        charterCompany: charterCompany.trim() || undefined,
        notes: notes.trim() || undefined,
        invitedUserIds: invitedBuddies.map((b) => b.userId),
      };

      if (editing) {
        const saved = await scheduledDivesService.updateScheduledDive(editing.diveId, { ...basePayload, extra });
        onSaved(saved);
        onClose();
        return;
      }

      const openToPayload = initialTripId
        ? { tripId: initialTripId }
        : {
            openToGroupId: openTo === OpenTo.Group ? openToGroupId : undefined,
            openToClubId: openTo === OpenTo.Club ? openToClubId : undefined,
          };

      // Additional dives in the same outing get created alongside the first,
      // paired via a shared sessionId so the calendar can group them
      const extraEntries = tripDives.filter((t) => t.siteName.trim() || t.linkedSiteSlug);
      if (extraEntries.length > 0) {
        const sessionId = newSessionId();
        const allEntries: TripDiveEntry[] = [{ siteName, linkedSiteSlug }, ...extraEntries];
        for (let i = 0; i < allEntries.length; i++) {
          const entry = allEntries[i];
          const entryExtra: Record<string, string> = {
            sessionId,
            sessionOrder: String(i + 1),
          };
          if (entry.linkedSiteSlug) {
            entryExtra.linkedSiteSlug = entry.linkedSiteSlug;
          }
          const saved = await scheduledDivesService.createScheduledDive({
            ...basePayload,
            ...openToPayload,
            siteName: entry.siteName.trim() || undefined,
            extra: entryExtra,
          });
          onSaved(saved);
        }
        onClose();
        return;
      }

      const saved = await scheduledDivesService.createScheduledDive({ ...basePayload, ...openToPayload, extra });
      onSaved(saved);
      onClose();
    } catch (err: any) {
      showError(err?.message ?? 'Failed to save dive.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit Scheduled Dive' : 'Schedule a Dive'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
          <DiveSitePickerField
            siteName={siteName}
            linkedSiteSlug={linkedSiteSlug}
            onSiteNameChange={setSiteName}
            onSiteLinked={handleSiteLinked}
          />
          {!editing && tripDives.map((trip, index) => (
            <Box key={index}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  DIVE {index + 2} — SAME TRIP
                </Typography>
                <IconButton
                  size="small"
                  aria-label={`Remove dive ${index + 2}`}
                  onClick={() => setTripDives((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
              <DiveSitePickerField
                siteName={trip.siteName}
                linkedSiteSlug={trip.linkedSiteSlug}
                onSiteNameChange={(name) =>
                  setTripDives((prev) => prev.map((t, i) => (i === index ? { ...t, siteName: name } : t)))
                }
                onSiteLinked={(site) =>
                  setTripDives((prev) => prev.map((t, i) => (i === index ? { ...t, linkedSiteSlug: site?.slug ?? null } : t)))
                }
              />
            </Box>
          ))}
          {!editing && (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => setTripDives((prev) => [...prev, { siteName: '', linkedSiteSlug: null }])}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              Add another dive to this trip
            </Button>
          )}
          {editing?.extra?.sessionId && (
            <Typography variant="caption" color="text.secondary">
              This dive is part of a multi-dive trip — edits here only affect this dive.
            </Typography>
          )}
          <TextField
            label="Charter Company"
            value={charterCompany}
            onChange={(e) => setCharterCompany(e.target.value)}
            placeholder="e.g. Force-E Scuba Centers"
            inputProps={{ maxLength: 200 }}
            fullWidth
          />
          {buddyOptions.length > 0 && (
            <Autocomplete
              multiple
              options={buddyOptions}
              getOptionLabel={(o) => o.displayName}
              value={invitedBuddies}
              onChange={(_, v) => setInvitedBuddies(v)}
              limitTags={isMobile ? 2 : 4}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.userId}
                    label={option.displayName}
                    size="small"
                    avatar={
                      <Avatar sx={{ bgcolor: 'primary.main', width: 20, height: 20, fontSize: '0.6rem' }}>
                        {option.displayName.slice(0, 1).toUpperCase()}
                      </Avatar>
                    }
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Invite Buddies" placeholder="Select dive buddies…" fullWidth />
              )}
              isOptionEqualToValue={(o, v) => o.userId === v.userId}
            />
          )}
          {initialTripId && (
            <Typography variant="caption" color="text.secondary">
              Who can see and join this dive follows the trip it's part of.
            </Typography>
          )}
          {!initialTripId && (groups.length > 0 || clubs.length > 0) && (
            <FormControl fullWidth size="small" disabled={!!editing}>
              <InputLabel>Open this dive to</InputLabel>
              <Select
                value={openTo}
                label="Open this dive to"
                onChange={(e) => setOpenTo(e.target.value as OpenToValue)}
              >
                <MenuItem value={OpenTo.Nobody}>Just my invited buddies</MenuItem>
                {groups.length > 0 && <MenuItem value={OpenTo.Group}>A specific dive group…</MenuItem>}
                {clubs.length > 0 && <MenuItem value={OpenTo.Club}>A specific club…</MenuItem>}
              </Select>
            </FormControl>
          )}
          {!initialTripId && !editing && openTo === OpenTo.Group && (
            <FormControl fullWidth size="small">
              <InputLabel>Dive Group</InputLabel>
              <Select value={openToGroupId} label="Dive Group" onChange={(e) => setOpenToGroupId(e.target.value)}>
                {groups.map((g) => (
                  <MenuItem key={g.groupId} value={g.groupId}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!initialTripId && !editing && openTo === OpenTo.Club && (
            <FormControl fullWidth size="small">
              <InputLabel>Club</InputLabel>
              <Select value={openToClubId} label="Club" onChange={(e) => setOpenToClubId(e.target.value)}>
                {clubs.map((c) => (
                  <MenuItem key={c.clubId} value={c.clubId}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!initialTripId && editing && (editing.openToGroupId || editing.openToClubId) && (
            <Typography variant="caption" color="text.secondary">
              This dive is open to {editing.openToGroupId ? 'a dive group' : 'a club'} — who it's open to can't be changed after creation.
            </Typography>
          )}
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={3}
            placeholder="Any details, meeting point, gear reminders…"
            inputProps={{ maxLength: 2000 }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving
            ? 'Saving…'
            : editing
              ? 'Save Changes'
              : tripDives.some((t) => t.siteName.trim() || t.linkedSiteSlug)
                ? `Schedule ${1 + tripDives.filter((t) => t.siteName.trim() || t.linkedSiteSlug).length} Dives`
                : 'Schedule Dive'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDiveDialog;
